/**
 * Falling shapes — drops the SVG shapes from above and lets them settle into a
 * natural pile using a real 2D physics simulation (matter.js). Pieces collide
 * and rest against one another rather than sitting in hand-placed positions.
 *
 * The shapes are rendered server-side in a static resting pile (see
 * templates/_exceptions/404.twig), so with no JS or a reduced-motion preference
 * the page still shows a sensible pile. Physics only takes over when allowed,
 * and matter.js is dynamically imported so it ships only with this page.
 */
function rand(min, max) {
    return min + Math.random() * (max - min)
}

// Convex hull vertices derived from each SVG's anchor points and analytically
// computed bezier extrema. Coordinates are in viewBox space then scaled to px.
function localVertices(type, w, h) {
    switch (type) {
        case 'tile': {
            // viewBox 0 0 145 117 — skewed parallelogram
            const [vw, vh] = [145, 117]
            return [
                [0,      46.50],  // left edge, upper end
                [4.50,   40.43],  // top-left corner inflection
                [136.72,  0.28],  // top edge, right end
                [144.91,  6.35],  // top-right corner
                [144.91, 109.92], // bottom-right corner
                [138.56, 116.27], // bottom edge, right end
                [6.35,   116.27], // bottom edge, left end
                [0,      109.92], // bottom-left corner
            ].map(([x, y]) => [x / vw * w, y / vh * h])
        }
        case 'dome': {
            // viewBox 0 0 156 149 — rotated wedge with two cubic-bezier arcs
            // Sampled at t=0, 0.25, 0.5, extremum (t≈0.622), 0.75, 1 on each arc
            // so the polygon closely follows the curved visual edges.
            const [vw, vh] = [156, 149]
            return [
                [23.40,  31.70],  // path start (upper-left)
                [44.06,  11.60],  // top arc t=0.25
                [69.78,   1.29],  // top arc t=0.5
                [83.25,   0.00],  // top arc min-y extremum (t≈0.622)
                [97.61,   1.43],  // top arc t=0.75
                [124.10, 12.60],  // top/right arc junction
                [144.37, 33.23],  // right arc t=0.25
                [154.52, 58.93],  // right arc t=0.5
                [155.80, 72.40],  // right arc max-x extremum (t≈0.622)
                [154.54, 86.73],  // right arc t=0.75
                [143.20, 113.20], // right arc end (lower-right)
                [120.90, 146.00], // bottom-right
                [112.10, 147.70], // bottom
                [2.78,   73.30],  // left anchor
                [0.00,   68.10],  // left-corner bezier min-x extremum
                [1.10,   64.50],  // upper-left end
            ].map(([x, y]) => [x / vw * w, y / vh * h])
        }
        case 'house': {
            // viewBox 0 0 172 129 — irregular polygon
            const [vw, vh] = [172, 129]
            return [
                [31.68,   0.71],
                [27.80,   2.09],
                [130.10,  1.46],
                [136.45,  1.74],
                [170.74, 57.52],
                [114.65, 126.54],
                [105.66, 127.46],
                [2.37,   43.51],
                [1.43,   34.53],
            ].map(([x, y]) => [x / vw * w, y / vh * h])
        }
        default: { // pentagon
            // viewBox 0 0 150 155 — tilted gem
            // Right-arc and bottom-arc extrema at t≈0.532
            const [vw, vh] = [150, 155]
            return [
                [93.38,   0.25],
                [99.42,   1.65],
                [149.33, 49.43], // right-arc maximum-x extremum
                [147.68, 54.01],
                [58.84,  151.94],
                [54.17,  154.10], // bottom-arc maximum-y extremum
                [49.88,  152.37],
                [0.12,   103.08],
                [2.08,   109.02],
                [15.33,   26.52],
                [19.78,   21.66],
            ].map(([x, y]) => [x / vw * w, y / vh * h])
        }
    }
}

async function initFallingShapes() {
    const container = document.querySelector('[data-falling-shapes]')
    if (!container) return

    const els = Array.from(container.querySelectorAll('[data-shape]'))
    if (!els.length) return

    // No motion: leave the server-rendered static pile in place.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    // Hide the static pile while the physics engine loads, so it doesn't flash
    // before the drop. Restore it if anything goes wrong.
    els.forEach((el) => (el.style.opacity = '0'))

    let Matter
    try {
        const mod = await import('matter-js')
        Matter = mod.default ?? mod
    } catch {
        els.forEach((el) => (el.style.opacity = '1'))
        return
    }

    const { Engine, Runner, Bodies, Body, Composite, Vertices, Events, Mouse, MouseConstraint, Query } = Matter

    // Expand the container to fill the full viewport so dragging works anywhere
    container.style.height = `${window.innerHeight}px`

    const width = container.clientWidth
    const height = container.clientHeight

    const engine = Engine.create()
    engine.world.gravity.y = 1.1
    engine.enableSleeping = true

    // Build a body per shape, measuring each element's rendered size first.
    const items = els
        .map((el) => {
            const w = el.offsetWidth
            const h = el.offsetHeight
            if (!w || !h) return null

            // Pre-compute the convex hull so our centroid offset matches
            // what Bodies.fromVertices uses internally.
            const rawVerts = localVertices(el.dataset.shape, w, h).map(([x, y]) => ({ x, y }))
            const verts = Vertices.hull(Vertices.clockwiseSort(rawVerts))
            const centre = Vertices.centre(verts)

            const startX = rand(w, width - w)
            const startY = -rand(100, height + 1400)

            const body = Bodies.fromVertices(
                startX,
                startY,
                [verts],
                {
                    restitution: 0.2,
                    friction: 0.5,
                    frictionStatic: 0.8,
                    density: 0.001,
                    sleepThreshold: 30,
                },
                true,
            )
            if (!body) return null

            Body.setAngle(body, rand(-0.4, 0.4))
            Body.setAngularVelocity(body, rand(-0.06, 0.06))

            // Offset of the silhouette centroid from the element box centre,
            // so we can map the body (positioned at its centroid) back onto the
            // full-box element.
            const offset = { x: centre.x - w / 2, y: centre.y - h / 2 }

            // Re-anchor the element to the top-left and drive it via transform.
            el.style.left = '0'
            el.style.top = '0'
            el.style.bottom = 'auto'
            el.style.marginLeft = `${-w / 2}px`
            el.style.marginTop = `${-h / 2}px`
            el.style.transformOrigin = 'center'
            el.style.opacity = '1'

            return { el, body, offset }
        })
        .filter(Boolean)

    if (!items.length) {
        els.forEach((el) => (el.style.opacity = '1'))
        return
    }

    // Static boundaries: floor at the container bottom, walls at each edge.
    const wall = { isStatic: true }
    const floor = Bodies.rectangle(width / 2, height + 100, width + 400, 200, wall)
    const leftWall = Bodies.rectangle(-100, 0, 200, 8000, wall)
    const rightWall = Bodies.rectangle(width + 100, 0, 200, 8000, wall)

    Composite.add(engine.world, [
        ...items.map((it) => it.body),
        floor,
        leftWall,
        rightWall,
    ])

    function sync() {
        for (const { el, body, offset } of items) {
            const a = body.angle
            const cos = Math.cos(a)
            const sin = Math.sin(a)
            const x = body.position.x - (offset.x * cos - offset.y * sin)
            const y = body.position.y - (offset.x * sin + offset.y * cos)
            el.style.transform = `translate(${x}px, ${y}px) rotate(${a}rad)`
        }
    }
    Events.on(engine, 'afterUpdate', sync)
    sync()

    // Bind mouse to the root <html> element so events are captured regardless of
    // z-index, aria-hidden, or pointer-events on the shape container.
    // Then offset to translate viewport coords → container-local coords.
    const mouse = Mouse.create(document.documentElement)
    const syncOffset = () => {
        const r = container.getBoundingClientRect()
        Mouse.setOffset(mouse, { x: -r.left, y: -r.top })
    }
    syncOffset()
    window.addEventListener('resize', syncOffset)
    window.addEventListener('scroll', syncOffset, { passive: true })

    // matter-js binds its own listeners to the element above and calls
    // preventDefault() unconditionally. On <html> that swallows page-wide wheel
    // scrolling, and — since a cancelled touchstart never produces a click —
    // every tap on the page, links included. Drop them and re-bind touch below.
    mouse.element.removeEventListener('wheel', mouse.mousewheel)
    mouse.element.removeEventListener('touchstart', mouse.mousedown)
    mouse.element.removeEventListener('touchmove', mouse.mousemove)
    mouse.element.removeEventListener('touchend', mouse.mouseup)

    // Same maths as Mouse._getRelativeMousePosition, so the hit test below
    // agrees with the position MouseConstraint reads.
    const touchPoint = (event) => {
        const touch = event.changedTouches[0]
        const bounds = mouse.element.getBoundingClientRect()
        return {
            x: touch.pageX - bounds.left - window.scrollX + mouse.offset.x,
            y: touch.pageY - bounds.top - window.scrollY + mouse.offset.y,
        }
    }

    // Only claim a touch that lands on a shape (matter's handlers preventDefault
    // for us); anything else stays an ordinary tap or scroll.
    const bodies = items.map((it) => it.body)
    let touchDragging = false

    mouse.element.addEventListener(
        'touchstart',
        (event) => {
            if (!Query.point(bodies, touchPoint(event)).length) return
            touchDragging = true
            mouse.mousedown(event)
        },
        { passive: false },
    )

    mouse.element.addEventListener(
        'touchmove',
        (event) => {
            if (!touchDragging) return
            mouse.mousemove(event)
        },
        { passive: false },
    )

    const endTouch = (event) => {
        if (!touchDragging) return
        touchDragging = false
        mouse.mouseup(event)
    }
    mouse.element.addEventListener('touchend', endTouch, { passive: false })
    mouse.element.addEventListener('touchcancel', endTouch, { passive: false })

    const mc = MouseConstraint.create(engine, {
        mouse,
        constraint: { stiffness: 0.2, damping: 0.1 },
    })
    Composite.add(engine.world, mc)

    // Cursor feedback on the dragged shape
    items.forEach(({ el }) => {
        el.style.cursor = 'grab'
        el.style.pointerEvents = 'auto'
    })
    Events.on(mc, 'startdrag', ({ body }) => {
        const hit = items.find((it) => it.body === body)
        if (hit) hit.el.style.cursor = 'grabbing'
    })
    Events.on(mc, 'enddrag', ({ body }) => {
        const hit = items.find((it) => it.body === body)
        if (hit) hit.el.style.cursor = 'grab'
    })

    const runner = Runner.create()
    Runner.run(runner, engine)

    // Keep walls aligned on resize
    let resizeTimer
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => {
            container.style.height = `${window.innerHeight}px`
            const w = container.clientWidth
            const h = container.clientHeight
            Body.setPosition(floor, { x: w / 2, y: h + 100 })
            Body.setPosition(rightWall, { x: w + 100, y: 0 })
        }, 200)
    })
}

if (document.readyState !== 'loading') {
    initFallingShapes()
} else {
    document.addEventListener('DOMContentLoaded', initFallingShapes)
}
